import { canAccessSegment } from 'app/utils'
import { RSSFeedIcon } from 'components/icons/rssfeed'
import { TwitterIcon } from 'components/icons/twittersmall'
import { Loading } from 'components/loading'
import { RasaContext } from 'context'
import { Dataset } from 'generic/dataset'
import * as React from 'react'
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { BillingPlanDetailCode } from 'shared_server_client/types/billing_plan'
import { SourceTypes } from '../../../shared_server_client/constants'
import { isValidUpdatedSourceName } from '../utility/utils'
import './edit-source.scss'
import { LoadSegmentCodes } from 'components/common/load-partner-codes/component'
import { DEFAULT_SEGMENT_CODE } from '../constants'

export interface Source {
  source_type: string,
  name: string,
  source_url: string,
  id: string|number,
  community_partner_code: string,
  newsbrief_count?: number,
}

interface EditSourceModalProps {
  isOpen: boolean,
  sourceData: Source,
  onClose: () => void,
  onSave: () => void,
}

interface EditSourceModalState {
  name: string,
  existingSources: any[],
  communityId?: string,
  hasSegmentAccess: boolean
  selectedSegmentCode: string,
  segments: any[],
  isLoading: boolean,
  isSaving: boolean,
  newsbriefCount: string,
}

export class EditSourceModal extends React.Component<EditSourceModalProps, EditSourceModalState> {
  public static contextType = RasaContext
  public static key: string = 'editSourceModal'

  constructor(props: EditSourceModalProps) {
    super(props)
    this.state = {
      existingSources: [],
      hasSegmentAccess: false,
      isLoading: false,
      isSaving: false,
      name: '',
      selectedSegmentCode: DEFAULT_SEGMENT_CODE,
      segments: [],
      newsbriefCount: '',
    }
  }

  public componentDidUpdate(prevProps: EditSourceModalProps) {
    // Load data when modal opens
    if (this.props.isOpen && !prevProps.isOpen && this.props.sourceData) {
      this.loadSourceData()
    }
  }

  private loadSourceData = () => {
    if (!this.props.sourceData || !this.props.sourceData.id) {
      return
    }

    this.setState({ isLoading: true })

    this.context.user.init().then(({activeCommunity}) => {
      const avlFeatures: BillingPlanDetailCode[] = activeCommunity.billingInfo.currentPlan.features || []

      Promise.all([
        new Dataset().loadCommunityDataset('communitySources', activeCommunity.communityId),
        canAccessSegment(avlFeatures, activeCommunity.communityId)
      ]).then(([sourcesResponse, canAccess]) => {
        const sources = sourcesResponse[0] || []

        this.setState({
          communityId: activeCommunity.communityId,
          hasSegmentAccess: canAccess,
          existingSources: sources,
          name: this.props.sourceData.name,
          selectedSegmentCode: this.props.sourceData.community_partner_code || DEFAULT_SEGMENT_CODE,
          newsbriefCount: this.props.sourceData.newsbrief_count != null && this.props.sourceData.newsbrief_count !== -1
            ? String(this.props.sourceData.newsbrief_count)
            : '',
          isLoading: false,
        })
      })
    })
  }

  public render() {
    if (!this.props.isOpen || !this.props.sourceData) {
      return null
    }

    return (
      <Modal isOpen={this.props.isOpen} toggle={this.props.onClose} size="lg">
        <ModalHeader toggle={this.props.onClose}>
          Edit Source
        </ModalHeader>
        <ModalBody>
          {this.renderContent()}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={this.props.onClose} disabled={this.state.isSaving}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={this.handleSave}
            disabled={this.state.isSaving || this.saveDisabled()}
          >
            {this.state.isSaving ? 'Saving...' : 'Save'}
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  private renderContent() {
    if (this.state.isLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Loading size="64"/>
        </div>
      )
    }

    return (
      <div className="edit-source-modal-content">
        <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
          <div className="heading" style={{ flexShrink: 0, width: '150px' }}>
            <h5>Source Type</h5>
          </div>
          <div style={{ flexGrow: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', paddingTop: '8px' }}>
              {this.props.sourceData.source_type === 'Twitter' ?
              <TwitterIcon svgwidth={20} svgheight={20}/> :
              <RSSFeedIcon svgwidth={20} svgheight={20}/> }
              <strong style={{ marginLeft: '8px' }}>{this.props.sourceData.source_type}</strong>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
          <div className="heading" style={{ flexShrink: 0, width: '150px' }}>
            <h5>Source URL</h5>
          </div>
          <div style={{ flexGrow: 1, paddingTop: '8px' }}>
            <a href={this.sourceUrl()} target="_blank" rel="noopener">{this.sourceUrlText()}</a>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
          <div className="heading" style={{ flexShrink: 0, width: '150px' }}>
            <h5>Source Name</h5>
          </div>
          <div style={{ flexGrow: 1 }}>
            <Input
              type="text"
              value={this.state.name}
              onChange={(e) => this.setState({name: e.target.value})}
            />
            {!this.state.isLoading && !!this.state.existingSources.length &&
            !isValidUpdatedSourceName(this.state.existingSources, this.state.name, String(this.props.sourceData.id)) &&
            <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
              You already have a source with this name
            </div>}
          </div>
        </div>

        {this.state.hasSegmentAccess &&
          <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
            <div className="heading" style={{ flexShrink: 0, width: '150px' }}>
              <h5>Segment</h5>
            </div>
            <div style={{ flexGrow: 1 }}>
              <LoadSegmentCodes
                segmentCode={this.state.selectedSegmentCode}
                setSegmentCode={this.segmentCodeChanged}
                setSegmentData={this.setSegmentData}
                hideLabel={true}
              />
            </div>
          </div>
        }

        <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
          <div className="heading" style={{ flexShrink: 0, width: '150px' }}>
            <h5>Max Articles</h5>
          </div>
          <div style={{ flexGrow: 1 }}>
            <Input
              type="number"
              value={this.state.newsbriefCount}
              onChange={this.handleNewsbriefCountChange}
              placeholder="Leave empty for no limit"
            />
          </div>
        </div>
      </div>
    )
  }

  private segmentCodeChanged = (code: any) => {
    this.setState({
      selectedSegmentCode: code.key,
    })
  }

  private setSegmentData = (segments: any[]) => {
    this.setState({
      segments,
    })
  }

  private handleNewsbriefCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // Allow empty string or valid integers only
    if (value === '' || /^\d+$/.test(value)) {
      this.setState({ newsbriefCount: value })
    }
  }

  private sourceUrlText() {
    switch (this.props.sourceData.source_type) {
      case SourceTypes.twitter : return `@${this.props.sourceData.source_url}`
      default : return this.props.sourceData.source_url
    }
  }

  private sourceUrl() {
    switch (this.props.sourceData.source_type) {
      case SourceTypes.rss : return this.props.sourceData.source_url
      case SourceTypes.twitter : return `https://www.twitter.com/${this.props.sourceData.source_url}`
      default: return null
    }
  }

  private saveDisabled(): boolean {
    return !this.state.isLoading && !!this.state.existingSources.length &&
           !isValidUpdatedSourceName(this.state.existingSources, this.state.name, String(this.props.sourceData.id))
  }

  private handleSave = () => {
    this.setState({ isSaving: true })

    this.context.entityMetadata.getEntityObject('communitySource')
      .then((entity) => {
        entity.setRecordId(this.state.communityId, this.props.sourceData.id)
        entity.data.name = this.state.name
        if (this.state.selectedSegmentCode) {
          entity.data.community_partner_code = this.state.selectedSegmentCode
        }
        // Set newsbrief_count: -1 if empty, otherwise parse as integer
        entity.data.newsbrief_count = this.state.newsbriefCount === '' ? -1 : parseInt(this.state.newsbriefCount, 10)
        return entity.save()
      })
      .then(() => {
        this.setState({ isSaving: false })
        this.props.onSave()
      })
      .catch(() => {
        this.setState({ isSaving: false })
      })
  }
}
